import { MutableRefObject } from 'react';
import { CallToActionKind, CallToActionKindType, valueof } from '@/types';

import { AppDispatch } from '@/redux';
import { validateCurrentCheckoutStepOrRedirect } from '@/redux/thunks/checkout/validateCurrentCheckoutStepOrRedirect';

import styles from '@/components/checkout/section/CheckoutSidebarSection.module.scss';
import { AddressModel, CartPayModeModel, CartShippingModel } from '@/models';
import { PaymentProviderShape } from '@/redux/slices/checkout.slice';
import { isObjectEmpty } from '@/utils/comparisonUtils';
import { COUNTRIES } from '@/constants/profile';
import { PAYMENT_PLATFORMS, STEPS } from '@/constants/checkout';

export const useSidebarContinueButton = (params: HookParams): HookReturn => {
  const {
    t, dispatch, buttonRef, isUserConnected, isMobile, currentStep,
    paymentProvider, shippingMode, paymentMode, userHasAcceptedCgv,
    prearrivalShippingMode, primeurShippingMode, shippingMap, isAddressOk, isUsa
  } = params;

  function validateStep() {
    if (currentStep === 'pay' && (
      paymentProvider.label === PAYMENT_PLATFORMS.PAY_JUNCTION ||
      paymentProvider.label === PAYMENT_PLATFORMS.CSR_PAY_JUNCTION
    )) {
      return;
    }
    dispatch(validateCurrentCheckoutStepOrRedirect({ type: 'validateAndRedirect', checkAddressForRedirect: true }));
  }

  function computeFormId() {
    if (paymentProvider.label === PAYMENT_PLATFORMS.PAY_JUNCTION) {
      return 'payjunction-form';
    }
    if (paymentProvider.label === PAYMENT_PLATFORMS.CSR_PAY_JUNCTION) {
      return 'payjunction-csr-form';
    }
  }

  function disabledInShippingModeIf() {
    if (!shippingMode) return true;
    if (shippingMode.isDrive && shippingMode.pickUpLater) return false;
    if (shippingMode.isDrive && isObjectEmpty(shippingMode.bookingSlot)) return true;
    if ((shippingMode.isDrive || shippingMode.isTopChrono || (shippingMode.isSchedulable && shippingMode.deliveryDates)) && !(
      (shippingMode.bookingSlot?.start && shippingMode.bookingSlot?.end) ||
      (!shippingMode.pickUpLater)
    )) return true;
    if ((shippingMode.isTopChrono || (shippingMode.isSchedulable && shippingMode.deliveryDates)) &&
      !shippingMode.bookingSlot?.phoneNumber) return true;
  }

  function disabledInShippingModeInSingapourIf() {
    if (!isUsa && ((shippingMap.PA && !prearrivalShippingMode) ||
      (shippingMap.P && !primeurShippingMode))) {
      return true;
    }

    if (shippingMap.L) {
      return disabledInShippingModeIf();
    }
  }

  const commonButtonPropsForAllSteps = {
    kind: CallToActionKind.PRIMARY,
    onClick: validateStep,
    shouldShowLoadingState: true,
    buttonRef,
    id: 'continue',
    fullWidth: true,
    form: currentStep === 'pay' ? computeFormId() : '',
    className: isMobile ? styles['sticky-button'] : ''
  } as const;

  const specificsButtonPropsForStep = () => {
    const props = {
      cart: {
        props: { ...commonButtonPropsForAllSteps },
        label: t(isUserConnected ? 'btn.continue' : 'checkout:cart.signinAndPay')
      },
      address: {
        props: {
          ...commonButtonPropsForAllSteps,
          disabled: !isAddressOk
        },
        label: t('btn.continue')
      },
      billing: {
        props: {
          ...commonButtonPropsForAllSteps,
          disabled: !isAddressOk
        },
        label: t('btn.continue')
      },
      shipping: {
        props: {
          ...commonButtonPropsForAllSteps,
          disabled: disabledInShippingModeInSingapourIf()
        },
        label: t('btn.continue')
      },
      review: {
        props: { ...commonButtonPropsForAllSteps },
        label: t('checkout:cartReview.proceed')
      },
      pay: {
        props: {
          ...commonButtonPropsForAllSteps,
          disabled: isObjectEmpty(paymentMode) || !paymentProvider.isReadyToPay || !userHasAcceptedCgv
        },
        label: t('checkout:cart.pay')
      },
      report: {
        props: { ...commonButtonPropsForAllSteps },
        label: t('checkout:cart.return')
      }
    };
    return props[currentStep];
  };

  return specificsButtonPropsForStep();
};

type HookParams = {
  t: (key: string) => string;
  dispatch: AppDispatch;
  buttonRef: MutableRefObject<HTMLButtonElement | null>;
  isUserConnected: boolean;
  isMobile: boolean;
  currentStep: valueof<typeof STEPS>;
  paymentProvider: PaymentProviderShape;
  isAddressOk: boolean;
  billingAddress: AddressModel;
  shippingMode: CartShippingModel;
  prearrivalShippingMode: CartShippingModel;
  primeurShippingMode: CartShippingModel;
  paymentMode: CartPayModeModel;
  userHasAcceptedCgv: boolean;
  isUsa: boolean;
  shippingMap: Record<string, CartShippingModel[]>
}

type HookReturn = {
  props: {
    kind: CallToActionKindType,
    onClick: () => void,
    shouldShowLoadingState: boolean,
    buttonRef: MutableRefObject<HTMLButtonElement | null>,
    fullWidth: boolean,
    form?: string,
    className: string,
    disabled?: boolean
  };
  label: string;
}
